import { PLANS, pieceClassement, planDe, type Rubrique, type TypeEntite } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api, corps, lire, requetes } from '../api'
import { cheminLisible, LIBELLES_RUBRIQUE } from '../libelles'
import { Formulaire } from './Formulaire'

// Classement d'une pièce dans le plan de classement de son entité : placée par Claude d'après
// le plan, choisie ici quand il hésite (les dossiers entre lesquels il hésite, d'un geste, ou un
// autre, cherché en tapant), corrigée s'il s'est trompé ; un dossier qu'il propose se valide ou
// se refuse ici aussi. Une pièce déjà rangée suit dans le Drive. Sans racine de
// classement, l'entité garde ses anciens chemins, par rubrique.

type PieceAClasser = {
  id: string
  entiteId: string | null
  entiteType: TypeEntite | null
  racineClassement: string | null
  rubrique: Rubrique | null
  dossierId: string | null
  /** Placement incertain : les dossiers entre lesquels Claude hésite. */
  dossiersHesites?: string[] | null
  exerciceConcerne: number | null
}

type Options = {
  p: PieceAClasser
  ecriture: boolean
  /** Le choix du dossier déjà ouvert (ouvert depuis le fil d'Ariane Drive). */
  ouvertAuDepart?: boolean
  /** Choix fermé : classée, ou annulé. */
  onFermer?: () => void
}

export function ClasserPiece({ p, ...options }: Options) {
  if (p.racineClassement && p.entiteId)
    return <ClasserDansLePlan p={{ ...p, entiteId: p.entiteId }} {...options} />
  return <ClasserParRubrique p={p} {...options} />
}

const champExercice = {
  nom: 'exerciceConcerne',
  libelle: 'Exercice concerné',
  type: 'entier' as const,
  aide: 'Seulement s’il diffère de l’année du document (liasse 2025 reçue en 2026 : 2025).',
}

function ClasserDansLePlan({
  p,
  ecriture,
  ouvertAuDepart = false,
  onFermer,
}: Omit<Options, 'p'> & { p: PieceAClasser & { entiteId: string } }) {
  const client = useQueryClient()
  const [ouvert, ouvrir] = useState(ouvertAuDepart)
  const setOuvert = (o: boolean) => {
    ouvrir(o)
    if (!o) onFermer?.()
  }
  const [erreur, setErreur] = useState<string | null>(null)
  const plan = useQuery(requetes.plan(p.entiteId))
  const dossiers = plan.data?.dossiers ?? []
  const dossier = dossiers.find((d) => d.id === p.dossierId)
  const choix = dossiers
    .filter((d) => d.enService && d.recoitFichiers)
    .map((d) => ({ valeur: d.id, libelle: cheminLisible(d.chemin) }))
  // Dossiers où Claude hésite, encore en service et qui reçoivent des fichiers, dans son ordre.
  const hesites = dossier
    ? []
    : (p.dossiersHesites ?? [])
        .map((id) => dossiers.find((d) => d.id === id))
        .filter((d) => d !== undefined && d.enService && d.recoitFichiers)
        .map((d) => d!)
  const classerDans = async (dossierId: string) => {
    try {
      setErreur(null)
      await lire(
        api.pieces[':id'].classer.$post({
          param: { id: p.id },
          json: corps(pieceClassement, { dossierId }),
        }),
      )
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Classement impossible.')
    }
  }
  const decider = async (quoi: 'valider' | 'refuser') => {
    try {
      setErreur(null)
      await lire(api.dossiers[':id'][quoi].$post({ param: { id: dossier!.id } }))
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Décision impossible.')
    }
  }
  const formulaire = (
    <>
      <Formulaire
        champs={[
          {
            nom: 'dossierId',
            libelle: 'Dossier du plan',
            type: 'choix',
            requis: true,
            cherchable: true,
            options: choix,
          },
          champExercice,
        ]}
        initial={{
          dossierId: dossier?.enService ? dossier.id : '',
          exerciceConcerne: p.exerciceConcerne,
        }}
        libelleBouton="Classer"
        onValider={async (v) => {
          await lire(
            api.pieces[':id'].classer.$post({
              param: { id: p.id },
              json: corps(pieceClassement, v),
            }),
          )
          await client.invalidateQueries()
          setOuvert(false)
        }}
      />
      <button type="button" className="bouton-discret" onClick={() => setOuvert(false)}>
        Annuler
      </button>
    </>
  )
  // Ouvert depuis le fil d'Ariane Drive : le fil dit déjà où elle est, le choix seul.
  if (ouvertAuDepart && ouvert) return ecriture ? formulaire : null
  return (
    <>
      <p>
        {dossier?.statut === 'propose' ? (
          <span className="alerte">
            Dossier proposé par l’assistant : {cheminLisible(dossier.chemin)}
          </span>
        ) : dossier ? (
          <>
            Dossier : {cheminLisible(dossier.chemin)}
            {p.exerciceConcerne !== null && ` · exercice ${p.exerciceConcerne}`}
          </>
        ) : (
          <span className="alerte">Dossier de classement à choisir</span>
        )}{' '}
        <Link to="/entites/$id/plan" params={{ id: p.entiteId }} className="discret">
          Voir le plan
        </Link>
        {ecriture && !ouvert && (
          <>
            {' '}
            <button type="button" className="bouton-discret" onClick={() => setOuvert(true)}>
              {dossier?.statut === 'propose'
                ? 'Classer ailleurs'
                : dossier
                  ? 'Reclasser'
                  : hesites.length > 0
                    ? 'Un autre dossier'
                    : 'Choisir le dossier'}
            </button>
          </>
        )}
      </p>
      {hesites.length > 0 && (
        <div className="dossiers-hesites">
          <p>L’assistant hésite entre :</p>
          <ul className="liste-simple">
            {hesites.map((d) => (
              <li key={d.id}>
                {ecriture ? (
                  <button type="button" className="bouton" onClick={() => void classerDans(d.id)}>
                    {cheminLisible(d.chemin)}
                  </button>
                ) : (
                  <strong>{cheminLisible(d.chemin)}</strong>
                )}
                {d.role && <span className="discret"> {d.role}</span>}
              </li>
            ))}
          </ul>
          {erreur && <p className="alerte">{erreur}</p>}
        </div>
      )}
      {dossier?.statut === 'propose' && (
        <div className="dossier-proposition">
          <p>
            <strong>À quoi il servira :</strong> {dossier.role}
          </p>
          {dossier.motif && <p className="discret">{dossier.motif}</p>}
          {ecriture && (
            <div className="boutons">
              <button type="button" className="bouton" onClick={() => decider('valider')}>
                Valider le dossier
              </button>
              <button type="button" className="bouton-discret" onClick={() => decider('refuser')}>
                Refuser
              </button>
            </div>
          )}
          {erreur && <p className="alerte">{erreur}</p>}
        </div>
      )}
      {ecriture && ouvert && formulaire}
    </>
  )
}

function ClasserParRubrique({ p, ecriture, ouvertAuDepart = false, onFermer }: Options) {
  const client = useQueryClient()
  const [ouvert, ouvrir] = useState(ouvertAuDepart)
  const setOuvert = (o: boolean) => {
    ouvrir(o)
    if (!o) onFermer?.()
  }
  // Seules les rubriques qui ont une place dans le plan de l'entité (foyer ou société).
  const plan = PLANS[planDe(p.entiteType ?? 'societe')]
  const rubriques = (Object.keys(plan) as Rubrique[]).map((r) => ({
    valeur: r,
    libelle: LIBELLES_RUBRIQUE[r],
  }))
  const formulaire = (
    <>
      <Formulaire
        champs={[
          {
            nom: 'rubrique',
            libelle: 'Rubrique',
            type: 'choix',
            requis: true,
            options: rubriques,
          },
          champExercice,
        ]}
        initial={{ rubrique: p.rubrique ?? '', exerciceConcerne: p.exerciceConcerne }}
        libelleBouton="Classer"
        onValider={async (v) => {
          await lire(
            api.pieces[':id'].classer.$post({
              param: { id: p.id },
              json: corps(pieceClassement, v),
            }),
          )
          await client.invalidateQueries()
          setOuvert(false)
        }}
      />
      <button type="button" className="bouton-discret" onClick={() => setOuvert(false)}>
        Annuler
      </button>
    </>
  )
  if (ouvertAuDepart && ouvert) return ecriture ? formulaire : null
  return (
    <>
      <p>
        {p.rubrique ? (
          <>
            Rubrique : {LIBELLES_RUBRIQUE[p.rubrique]}
            {p.exerciceConcerne !== null && ` · exercice ${p.exerciceConcerne}`}
          </>
        ) : (
          <span className="alerte">Rubrique de classement à choisir</span>
        )}
        {ecriture && !ouvert && (
          <>
            {' '}
            <button type="button" className="bouton-discret" onClick={() => setOuvert(true)}>
              {p.rubrique ? 'Reclasser' : 'Classer'}
            </button>
          </>
        )}
      </p>
      {ecriture && ouvert && formulaire}
    </>
  )
}
